"use client";

import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  Chip,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import MarkunreadIcon from "@mui/icons-material/Markunread";
import DraftsIcon from "@mui/icons-material/Drafts";
import config from "../../../config";

export default function NotificationsList() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, [page, rowsPerPage]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const baseUrl = config.API_BASE_URL_V1;
      const response = await fetch(
        `${baseUrl}/notifications/all?page=${page + 1}&limit=${rowsPerPage}`,
        { headers: config.getHeaders() }
      );
      const result = await response.json();

      if (result.success) {
        setNotifications(result.data || []);
        // Get total from pagination object
        if (result.pagination?.total_records) {
          setTotal(result.pagination.total_records);
        }
        setError(null);
      } else {
        setError(result.message || "Failed to fetch notifications");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRead = async (notification_id, currentStatus) => {
    const newStatus = currentStatus === "unread" ? "read" : "unread";

    try {
      const baseUrl = config.API_BASE_URL_V1;
      const response = await fetch(
        `${baseUrl}/notifications/${notification_id}`,
        {
          method: "PATCH",
          headers: config.getHeaders(),
          body: JSON.stringify({ status: newStatus }),
        }
      );

      const result = await response.json();
      if (result.success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.notification_id === notification_id ? { ...n, status: newStatus } : n
          )
        );
      }
    } catch (err) {
      console.error("Error updating notification:", err);
    }
  };

  const handleMarkAllRead = async () => {
    // Mark all unread notifications as read
    const unreadNotifications = notifications.filter(n => n.status === 'unread');
    for (const notif of unreadNotifications) {
      await handleToggleRead(notif.notification_id, 'unread');
    }
  };

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ✅ Server-side pagination: no need to slice, data is already paginated from backend
  const filteredNotifications = notifications.filter(
    (n) =>
      (n.notification_title?.toLowerCase().includes(search.toLowerCase()) || false) ||
      (n.notification_detail?.toLowerCase().includes(search.toLowerCase()) || false)
  );

  return (
    <Box>
      {/* Header */}
      <AppBar
        position="static"
        elevation={1}
        sx={{
          backgroundColor: "var(--whiteColor)",
          color: "var(--blackColor)",
          boxShadow: "none",
          borderBottom: `1px solid var(--borderColor)`
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: "64px" }}>
          <form className="t-search-form">
            <label>
              <i className="material-symbols-outlined">search</i>
            </label>
            <input
              type="text"
              className="t-input"
              placeholder="Search here.."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </form>

          <Button
            variant="contained"
            sx={{ marginLeft: "auto" }}
            startIcon={<DoneAllIcon />}
            onClick={handleMarkAllRead}
          >
            Mark All as Read
          </Button>
        </Toolbar>
      </AppBar>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ color: "red" }}>Error: {error}</Typography>
        </Box>
      )}

      {/* Notifications List */}
      {!loading && !error && (
        <List sx={{ p: 0 }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography>No notifications found</Typography>
            </Box>
          ) : (
            notifications.map((n) => (
              <ListItem
                key={n.notification_id}
                divider
                sx={{
                  backgroundColor: n.status === "unread" ? "#f0f4ff" : "var(--whiteColor)",
                  borderBottom: `1px solid var(--borderColor)`,
                  "&:hover": { backgroundColor: "var(--lightColor)" }
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleToggleRead(n.notification_id, n.status)}
                  >
                    {n.status === "unread" ? <MarkunreadIcon color="error" /> : <DraftsIcon color="primary" />}
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "var(--blackColor)",
                            fontWeight: n.status === "unread" ? "bold" : "normal",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {n.notification_title}
                        </Typography>

                        <Chip
                          label={n.notification_type || "System"}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderColor: "var(--primaryColor)",
                            color: "var(--primaryColor)"
                          }}
                        />
                      </Box>
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" sx={{ color: "var(--bodyColor)" }}>
                        {n.notification_detail?.substring(0, 150)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "var(--bodyColor)" }}>
                        {new Date(n.created_date).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      )}

      {/* Pagination */}
      {!loading && !error && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
            backgroundColor: "var(--whiteColor)",
            borderTop: `1px solid var(--borderColor)`
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: "none", ".MuiToolbar-root": { minHeight: "auto", padding: 0 } }}
          />
        </Box>
      )}
    </Box>
  );
}
